import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function slugify(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function formatBRL(v: number | null | undefined): string {
  if (v == null) return "—";
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function formatInt(v: number | null | undefined): string {
  if (v == null) return "—";
  return v.toLocaleString("pt-BR");
}

export function formatPct(v: number | null | undefined, digits = 1): string {
  if (v == null) return "—";
  return `${v.toLocaleString("pt-BR", { maximumFractionDigits: digits, minimumFractionDigits: digits })}%`;
}

/**
 * Onda 19.11: normaliza phone pra formato E.164 com DDI 55 prefixado quando ausente.
 * Yampi devolve "11962519731" sem DDI; pra exibir/copy-paste consistente, usamos esse helper.
 *
 *   formatPhoneE164("11962519731")     → "5511962519731"
 *   formatPhoneE164("(11) 96251-9731") → "5511962519731"
 *   formatPhoneE164("5511962519731")   → "5511962519731"
 *   formatPhoneE164("+5511962519731")  → "5511962519731" (sem o "+")
 *   formatPhoneE164(null)              → null
 *
 * Sem o "+" no retorno — UI pode escolher se quer prefixar visualmente.
 * Mesma lógica do normalizePhone interno do nextags-client.
 */
export function formatPhoneE164(phone: string | null | undefined): string | null {
  if (!phone) return null;
  let digits = phone.replace(/\D/g, "");
  if (digits.length === 0) return null;
  // BR sem DDI (10-11 dígitos) → prefixa 55
  if (digits.length === 10 || digits.length === 11) digits = "55" + digits;
  else if (digits.length === 12 && !digits.startsWith("55")) digits = "55" + digits;
  if (digits.length < 10 || digits.length > 15) return null;
  return digits;
}

/** "há 2 minutos" / "há 3 horas" / "há 2 dias" — relativo a agora, em pt-BR */
export function timeAgo(date: Date | string | null | undefined, now: Date = new Date()): string {
  if (!date) return "—";
  const d = typeof date === "string" ? new Date(date) : date;
  const diffMs = now.getTime() - d.getTime();
  if (diffMs < 0) return "agora";
  const sec = Math.round(diffMs / 1000);
  if (sec < 45) return "agora mesmo";
  const min = Math.round(sec / 60);
  if (min < 60) return `há ${min} ${min === 1 ? "minuto" : "minutos"}`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `há ${hr} ${hr === 1 ? "hora" : "horas"}`;
  const day = Math.round(hr / 24);
  if (day < 30) return `há ${day} ${day === 1 ? "dia" : "dias"}`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `há ${mo} ${mo === 1 ? "mês" : "meses"}`;
  const yr = Math.round(mo / 12);
  return `há ${yr} ${yr === 1 ? "ano" : "anos"}`;
}

export function formatDateTime(d: Date | string | null | undefined): string {
  if (!d) return "—";
  const dt = typeof d === "string" ? new Date(d) : d;
  return dt.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * "1 morto" / "2 mortos" — número formatado + palavra concordando.
 *
 * v23.310 — resumos de admin montavam a frase com template literal e a
 * concordância ficava errada no singular ("1 mortos"). Como o número quase
 * sempre é 0 ou grande, o caso 1 passava despercebido.
 */
export function plural(n: number, singular: string, plural: string): string {
  return `${formatInt(n)} ${n === 1 ? singular : plural}`;
}
